import { useMemo, useRef, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { useAuth } from '../../../shared/useAuth.js';
import { formatIQD, formatNumber } from '../../../shared/format.js';
import { PrintLogo } from '../../../shared/PrintLogo.jsx';
import { useDiscounts } from '../hooks/useReports.js';
import { ExportButton } from './ExportButton.jsx';
import './print.css';

// أنواع العمليات — نفس مفاتيح الخادم (sale/manager/purchase).
const TYPE_LABELS = {
  sale: 'خصم فاتورة مبيعات',
  manager: 'خصم استثنائي (مدير)',
  purchase: 'خصم فاتورة مشتريات'
};

const TYPE_VARIANTS = {
  sale: 'info',
  manager: 'warning',
  purchase: 'muted'
};

const POS_TYPE_LABELS = {
  cafeteria: 'الكافتيريا',
  toys: 'الألعاب',
  checkout: 'المحاسبة',
  play_fee: 'رسوم اللعب',
  manual: 'يدوي'
};

const WEEKDAYS_AR = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const MONTHS_AR = [
  'كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران',
  'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'
];

const pad = (n) => String(n).padStart(2, '0');

function asDate(v) {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateAr(v) {
  const d = asDate(v);
  if (!d) return '—';
  return `${d.getDate()} ${MONTHS_AR[d.getMonth()]} ${d.getFullYear()}`;
}

function timeAr(v) {
  const d = asDate(v);
  if (!d) return '—';
  const h24 = d.getHours();
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${pad(h12)}:${pad(d.getMinutes())} ${h24 < 12 ? 'ص' : 'م'}`;
}

function dateTimeAr(v) {
  const d = asDate(v);
  if (!d) return '—';
  return `${WEEKDAYS_AR[d.getDay()]} ${dateAr(d)} — ${timeAr(d)}`;
}

/** النسبة: null تعني «غير متاحة» (لا أساس) — لا تُطبع 0% مضلّلة. */
function pctAr(value) {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${formatNumber(n, { decimals: 2 })}%`;
}

function typeLabel(t) {
  return TYPE_LABELS[t] || t || '—';
}

function referenceLabel(r) {
  if (r.reference) return r.reference;
  // فاتورة بلا رقم (بيانات قديمة): نعرض مرجع الجلسة أو مُعرّف الفاتورة
  // مختصراً — لا خانة فارغة أبداً.
  if (r.sessionId) return `جلسة ${String(r.sessionId).slice(-6)}`;
  if (r.invoiceId) return `#${String(r.invoiceId).slice(-6)}`;
  return '—';
}

function sourceLabel(r) {
  if (r.type === 'purchase') return 'مشتريات';
  if (r.type === 'manager') return 'رسوم اللعب';
  return POS_TYPE_LABELS[r.posType] || 'نقطة البيع';
}

function discountNameLabel(r) {
  if (r.discountRule && r.discountRule.name) return r.discountRule.name;
  if (r.type === 'manager') return 'خصم بإذن المدير';
  if (r.type === 'purchase') return 'خصم المورّد';
  if (r.lineDiscount > 0 && r.invoiceDiscount > 0) return 'خصم سطر + خصم فاتورة';
  if (r.lineDiscount > 0) return 'خصم على السطر';
  return 'خصم على الفاتورة';
}

function shiftLabel(r) {
  if (!r.shift) return '—';
  const who = r.shift.cashierName || '';
  const when = r.shift.openedAt ? dateAr(r.shift.openedAt) : '';
  return [who, when].filter(Boolean).join(' — ') || '—';
}

function KpiCard({ label, value, sub, accent }) {
  return (
    <div
      className="kpi-card"
      style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', background: '#fff' }}
    >
      <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 600 }}>{label}</div>
      <div
        style={{
          fontSize: 18, fontWeight: 800, marginTop: 4,
          color: accent || '#111827', letterSpacing: '-.02em'
        }}
      >
        {value}
      </div>
      {sub ? <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

export function DiscountsReport({ range }) {
  const user = useAuth((s) => s.user);
  const [userId, setUserId] = useState('');
  const [type, setType] = useState('');
  const [shiftId, setShiftId] = useState('');
  const tableRef = useRef(null);

  const { data, isLoading, error } = useDiscounts({ ...range, userId, type, shiftId });

  const rows = data?.rows || [];
  const totals = data?.totals || {
    count: 0, amountBefore: 0, discountTotal: 0, amountAfter: 0,
    byType: { sale: 0, manager: 0, purchase: 0 },
    countByType: { sale: 0, manager: 0, purchase: 0 },
    averagePercent: null
  };
  const facets = data?.facets || { users: [], shifts: [], types: [] };

  const periodLabel = useMemo(() => {
    const f = String(range?.from || '').slice(0, 10);
    const t = String(range?.to || '').slice(0, 10);
    if (!f && !t) return '';
    return f === t ? f : `${f} → ${t}`;
  }, [range]);

  // أعمدة التصدير — نفس ترتيب أعمدة الجدول وبنفس الأرقام المعروضة،
  // فلا يختلف ملف Excel/CSV/PDF عمّا يراه المستخدم على الشاشة.
  const exportColumns = [
    { key: 'date', label: 'التاريخ', value: (r) => dateAr(r.at) },
    { key: 'time', label: 'الوقت', value: (r) => timeAr(r.at) },
    { key: 'type', label: 'نوع العملية', value: (r) => typeLabel(r.type) },
    { key: 'source', label: 'الجهة', value: (r) => sourceLabel(r) },
    { key: 'reference', label: 'المستند', value: (r) => referenceLabel(r) },
    { key: 'session', label: 'الجلسة', value: (r) => (r.sessionId ? String(r.sessionId).slice(-6) : '') },
    { key: 'before', label: 'المبلغ قبل الخصم', value: (r) => r.amountBefore ?? 0 },
    { key: 'discount', label: 'قيمة الخصم', value: (r) => r.discountValue ?? 0 },
    { key: 'percent', label: 'النسبة %', value: (r) => (r.discountPercent == null ? '' : r.discountPercent) },
    { key: 'after', label: 'المبلغ بعد الخصم', value: (r) => r.amountAfter ?? 0 },
    { key: 'discountName', label: 'نوع/قاعدة الخصم', value: (r) => discountNameLabel(r) },
    { key: 'user', label: 'المستخدم', value: (r) => r.userName || 'غير معروف' },
    { key: 'customer', label: 'العميل', value: (r) => r.customerName || '' },
    { key: 'shift', label: 'الشفت', value: (r) => shiftLabel(r) },
    { key: 'reason', label: 'السبب / ملاحظات', value: (r) => r.reason || '' }
  ];

  const activeFilters = [
    userId ? `المستخدم: ${(facets.users.find((u) => u.userId === userId) || {}).name || userId}` : null,
    type ? `النوع: ${typeLabel(type)}` : null,
    shiftId
      ? `الشفت: ${(() => {
          const s = facets.shifts.find((x) => x.shiftId === shiftId);
          if (!s) return shiftId;
          return [s.cashierName, s.openedAt ? dateAr(s.openedAt) : ''].filter(Boolean).join(' — ');
        })()}`
      : null
  ].filter(Boolean);

  function resetFilters() {
    setUserId('');
    setType('');
    setShiftId('');
  }

  return (
    <div className="space-y-3" dir="rtl">
      {/* شريط الأدوات والترشيح — لا يُطبع */}
      <div className="no-print card p-3 flex flex-wrap items-center gap-2">
        <select
          className="input"
          style={{ maxWidth: 220 }}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          aria-label="المستخدم"
        >
          <option value="">كل المستخدمين</option>
          {facets.users.map((u) => (
            <option key={u.userId} value={u.userId}>{u.name}</option>
          ))}
        </select>

        <select
          className="input"
          style={{ maxWidth: 220 }}
          value={type}
          onChange={(e) => setType(e.target.value)}
          aria-label="نوع العملية"
        >
          <option value="">كل أنواع العمليات</option>
          {['sale', 'manager', 'purchase'].map((t) => (
            <option key={t} value={t}>{TYPE_LABELS[t]}</option>
          ))}
        </select>

        <select
          className="input"
          style={{ maxWidth: 260 }}
          value={shiftId}
          onChange={(e) => setShiftId(e.target.value)}
          aria-label="الشفت"
          // الشفت مرتبط بفواتير المبيعات فقط (فواتير المشتريات لا شفت لها).
          // لا نُعطّل القائمة وفيها اختيار قائم، وإلا عُلق المستخدم بترشيح
          // لا يستطيع إلغاءه بعد تغيير المدى إلى فترة بلا شفتات.
          disabled={!facets.shifts.length && !shiftId}
        >
          <option value="">
            {facets.shifts.length ? 'كل الشفتات' : 'لا شفتات في هذه الفترة'}
          </option>
          {shiftId && !facets.shifts.some((s) => s.shiftId === shiftId) ? (
            <option value={shiftId}>شفت مختار خارج هذه الفترة</option>
          ) : null}
          {facets.shifts.map((s) => (
            <option key={s.shiftId} value={s.shiftId}>
              {[s.cashierName, s.cashboxName, s.openedAt ? dateAr(s.openedAt) : '']
                .filter(Boolean)
                .join(' — ')}
            </option>
          ))}
        </select>

        {activeFilters.length ? (
          <button type="button" className="btn btn-ghost" onClick={resetFilters}>
            <Icon name="x" size={14} /> مسح الترشيح
          </button>
        ) : null}

        <div className="flex-1" />

        <button type="button" className="btn btn-primary" onClick={() => window.print()}>
          <Icon name="print" size={14} /> طباعة (A4)
        </button>
        <ExportButton
          rows={rows}
          columns={exportColumns}
          filename="discounts-report"
          title={`تقرير الخصومات — ${periodLabel}`}
          tableRef={tableRef}
        />
      </div>

      {error ? (
        <div className="card p-6 text-center text-red">تعذّر تحميل تقرير الخصومات</div>
      ) : null}

      <div className="print-root">
        {/* شعار المركز من الإعدادات — أعلى الورقة ووسطها */}
        <PrintLogo />
        {/* رأس التقرير */}
        <section
          className="print-section"
          style={{
            background: 'linear-gradient(135deg,#FF7A1A,#FFB627)',
            color: '#fff', borderRadius: 10, padding: 18, marginBottom: 14
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/kidzo-mark.png" alt="" width={44} height={44} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 800 }}>تقرير الخصومات</div>
              <div style={{ fontSize: 12, opacity: 0.9 }}>
                كل خصم مُطبَّق فعلاً: فواتير المبيعات، الخصم الاستثنائي على رسوم اللعب، وفواتير المشتريات
              </div>
            </div>
            <div style={{ textAlign: 'end', fontSize: 11 }}>
              <div>الفترة: <b>{periodLabel}</b></div>
              <div>عدد الخصومات: <b>{formatNumber(totals.count)}</b></div>
              {data?.period?.generatedAt ? <div>أُصدر: {dateTimeAr(data.period.generatedAt)}</div> : null}
              {user ? <div>المُصدِر: {user.fullName || user.username}</div> : null}
            </div>
          </div>
        </section>

        {activeFilters.length ? (
          <div
            className="print-section"
            style={{
              background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E',
              borderRadius: 10, padding: '8px 12px', marginBottom: 12,
              fontSize: 12, fontWeight: 600
            }}
          >
            ترشيح مُطبَّق — {activeFilters.join(' · ')}
          </div>
        ) : null}

        {/* الإجماليات */}
        <section className="print-section" style={{ marginBottom: 14 }}>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr))' }}>
            <KpiCard
              label="إجمالي الخصومات"
              value={formatIQD(totals.discountTotal)}
              sub={`${formatNumber(totals.count)} عملية خصم`}
              accent="#B91C1C"
            />
            <KpiCard
              label="المبالغ قبل الخصم"
              value={formatIQD(totals.amountBefore)}
              sub="أساس احتساب النسبة"
            />
            <KpiCard
              label="المبالغ بعد الخصم"
              value={formatIQD(totals.amountAfter)}
              sub="قبل − الخصم = بعد"
              accent="#15803D"
            />
            <KpiCard
              label="متوسط نسبة الخصم"
              value={pctAr(totals.averagePercent)}
              sub="إجمالي الخصم ÷ إجمالي ما قبله"
            />
          </div>

          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))', marginTop: 12 }}
          >
            {['sale', 'manager', 'purchase'].map((t) => (
              <KpiCard
                key={t}
                label={TYPE_LABELS[t]}
                value={formatIQD((totals.byType || {})[t] || 0)}
                sub={`${formatNumber((totals.countByType || {})[t] || 0)} عملية`}
              />
            ))}
          </div>
        </section>

        {/* الجدول */}
        <section className="print-section">
          <div style={{ marginBottom: 10, paddingBottom: 8, borderBottom: '2px solid #FF7A1A' }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>تفاصيل الخصومات</h3>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              الفواتير الملغاة ومسودّات المشتريات غير محتسبة — ولا تكرار لأي خصم
            </div>
          </div>

          {isLoading ? (
            <div className="text-ink-3 text-center py-10 text-sm">جاري تحميل تقرير الخصومات...</div>
          ) : rows.length === 0 ? (
            <div className="text-ink-3 text-center py-10 text-sm">
              لا توجد خصومات مطابقة في هذه الفترة
            </div>
          ) : (
            <div ref={tableRef} style={{ overflowX: 'auto' }}>
              <table dir="rtl">
                <thead>
                  <tr>
                    <th>التاريخ والوقت</th>
                    <th>نوع العملية</th>
                    <th>المستند</th>
                    <th>قبل الخصم</th>
                    <th>قيمة الخصم</th>
                    <th>النسبة</th>
                    <th>بعد الخصم</th>
                    <th>نوع/قاعدة الخصم</th>
                    <th>المستخدم</th>
                    <th>العميل</th>
                    <th>الشفت</th>
                    <th>السبب</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 600 }}>{dateAr(r.at)}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{timeAr(r.at)}</div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Pill variant={TYPE_VARIANTS[r.type] || 'muted'}>{typeLabel(r.type)}</Pill>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{sourceLabel(r)}</div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{referenceLabel(r)}</span>
                        {r.sessionId ? (
                          <div style={{ fontSize: 10.5, color: '#9ca3af' }}>
                            جلسة {String(r.sessionId).slice(-6)}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatIQD(r.amountBefore)}</td>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 800, color: '#B91C1C' }}>
                        {formatIQD(r.discountValue)}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {pctAr(r.discountPercent)}
                        {r.nominalPercent != null && r.nominalPercent !== r.discountPercent ? (
                          <div style={{ fontSize: 10.5, color: '#9ca3af' }}>
                            المعلنة {formatNumber(r.nominalPercent)}%
                          </div>
                        ) : null}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{formatIQD(r.amountAfter)}</td>
                      <td>{discountNameLabel(r)}</td>
                      <td style={{ fontWeight: 600 }}>{r.userName || 'غير معروف'}</td>
                      <td>{r.customerName || '—'}</td>
                      <td style={{ fontSize: 11.5 }}>{shiftLabel(r)}</td>
                      <td style={{ fontSize: 11.5, color: '#6b7280' }}>{r.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 800, background: '#FFF7ED' }}>
                    <td colSpan={3}>الإجمالي ({formatNumber(totals.count)} عملية)</td>
                    <td>{formatIQD(totals.amountBefore)}</td>
                    <td style={{ color: '#B91C1C' }}>{formatIQD(totals.discountTotal)}</td>
                    <td>{pctAr(totals.averagePercent)}</td>
                    <td>{formatIQD(totals.amountAfter)}</td>
                    <td colSpan={5} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
